import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Bell, MailWarning } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { listenForeground, syncPushToken } from "@/lib/push";

export default function AppShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications-count"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60_000,
  });
  const unread = data?.unread || 0;
  const showVerify = user && user.email_verified === false && !dismissed;

  useEffect(() => {
    syncPushToken();
    const onVis = () => document.visibilityState === "visible" && syncPushToken();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Foreground push: show a toast + refresh the unread badge while a tab is open.
  useEffect(() => {
    let unsub = () => {};
    listenForeground((payload) => {
      const n = payload?.notification || payload?.data || {};
      const title = n.title || "Debii";
      const body = n.body || "You have a new update";
      toast(title, { description: body });
      queryClient.invalidateQueries({ queryKey: ["notifications-count"] });
    }).then((fn) => {
      unsub = fn;
    });
    return () => unsub && unsub();
  }, [queryClient]);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/send-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch {
      toast.error("Couldn't send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="debii-shell pb-28">
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-md px-5 py-3.5 flex items-center justify-between border-b border-emerald-900/5">
        <button onClick={() => navigate("/")} aria-label="Debii home">
          <Logo size={30} />
        </button>
        <button
          data-testid="notifications-bell"
          onClick={() => navigate("/notifications")}
          className="relative h-10 w-10 rounded-full bg-secondary flex items-center justify-center transition-colors hover:bg-secondary/70"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        >
          <Bell className="h-5 w-5 text-primary" strokeWidth={2} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </header>
      {showVerify && (
        <div data-testid="verify-email-banner" className="mx-5 mt-3 flex items-center gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5">
          <MailWarning className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-900 flex-1">Please verify your email address.</p>
          <button data-testid="verify-email-resend" onClick={resend} disabled={sending} className="text-xs font-bold text-amber-700 underline underline-offset-2">
            {sending ? "Sending…" : "Resend"}
          </button>
          <button aria-label="Dismiss" onClick={() => setDismissed(true)} className="text-amber-500 text-sm px-1">✕</button>
        </div>
      )}
      <main className="px-5 pt-4">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}
